import { DateTime } from "luxon";
import type { IPrinter, IPrinterGroup, PrinterOption } from "~/types/printer";

export default () => {
  const { t } = useI18n();
  const modalOptions = ref<{ show: boolean; entity: any }>({
    show: false,
    entity: null,
  });
  const groups = ref<IPrinterGroup[]>([{ label: t("all_printers"), value: "all", printers: [] }]);

  const getPrinters = async () => {
    const latestActiveAt30Minutes = DateTime.utc().minus({ minutes: 30 }).toFormat("yyyy-MM-dd HH:mm");
    const { t } = useI18n();
    const printers = ref<PrinterOption[]>([]);
    const loading = ref(true);

    homeFetch<IPrinter[]>(`workspace/printers`, {
      query: {
        filters: {
          active_at: {
            $or: [{ $gte: latestActiveAt30Minutes }],
          },
        },
        sort: {
          0: "'default':desc", //Must be wrapped in quotes to avoid it being parsed as keyword in the query
          1: "active_at:desc",
        },
      },
    })
      .then((response) => {
        if (!response.data || !response.data.length) return;

        printers.value = response.data.map((printer: IPrinter) => ({
          label: printer.name,
          subtext: t("last_seen", { date: printer.active_at }),
          value: printer.alias,
          data: printer,
          id: printer.id,
        })) as PrinterOption[];
        groups.value[0]!.printers = printers.value;
      })
      .finally(() => {
        loading.value = false;
      });

    const hasPrinters = computed(() => printers.value.length > 0 && !loading.value);
    return {
      data: printers,
      hasPrinters,
      pending: loading,
    };
  };
  const getPrinterGroups = async () => {
    const { t } = useI18n();
    const loading = ref(true);

    homeFetch<IPrinterGroup[]>("workspace/printers/groups")
      .then((response) => {
        if (response?.data) {
          if (groups.value.length > 1) {
            groups.value.splice(1); // Clear existing groups except the first one
          }
          for (const group of response.data) {
            group.printers = (group.printers as unknown as IPrinter[]).map((printer: IPrinter) => ({
              label: printer.name,
              subtext: t("last_seen", { date: printer.active_at }),
              value: printer.alias,
              data: printer,
              id: printer.id,
            })) as PrinterOption[];
            groups.value.push(group);
          }
        }
      })
      .finally(() => {
        loading.value = false;
      });

    const hasGroups = computed(() => groups.value.length > 1 && !loading.value);

    return {
      data: groups,
      hasGroups,
      pending: loading,
    };
  };

  const printLabel = async (printer: string, package_number: string) => {
    //generate a unique id for the toast group
    const toastId = Math.random().toString(36).substring(7);
    modalOptions.value.show = false;

    homeFetch(`shipments/print`, {
      method: "GET",
      query: {
        package_number,
        alias: encodeURIComponent(printer),
      },
      silent: true,
    })
      .then(() => {
        toastAdd({
          id: toastId,
          group: "print:shipment:label",
          text: t("printer.printing_label", 1),
        });
      })
      .catch(() => {
        toastAdd({
          id: toastId,
          group: "print:shipment:label",
          text: t("printer.error_printing"),
          error: true,
          duration: 10000,
        });
      });
  };

  const printTransfer = async (printer: string, transferId: number) => {
    //generate a unique id for the toast group
    const toastId = Math.random().toString(36).substring(7);
    modalOptions.value.show = false;

    homeFetch(`transfers/${transferId}/print/${encodeURIComponent(printer)}`, {
      method: "GET",
      silent: true,
    })
      .then(() => {
        toastAdd({
          id: toastId,
          group: "print:shipment:label",
          text: t("printer.printing_label", 1),
        });
      })
      .catch(() => {
        toastAdd({
          id: toastId,
          group: "print:shipment:label",
          text: t("printer.error_printing"),
          error: true,
          duration: 10000,
        });
      });
  };

  const printConsignment = async (printer: string, consignmentNumber: string) => {
    //generate a unique id for the toast group
    const toastId = Math.random().toString(36).substring(7);
    modalOptions.value.show = false;

    homeFetch(`consignments/print`, {
      method: "GET",
      query: {
        consignment_number: consignmentNumber,
        alias: encodeURIComponent(printer),
      },
      silent: true,
    })
      .then(() => {
        toastAdd({
          id: toastId,
          group: "print:consignment:label",
          text: t("printer.printing_label", 1),
        });
      })
      .catch(() => {
        toastAdd({
          id: toastId,
          group: "print:consignment:label",
          text: t("printer.error_printing"),
          error: true,
          duration: 10000,
        });
      });
  };

  const printBulkLabels = async (printer: string, selectAll: boolean, selected: number[] | string[], filters: any) => {
    const body = buildBulkActionBody({ action: "printer", selectAll, selected, filters, options: { printer } });

    //generate a unique id for the toast group
    const toastId = Math.random().toString(36).substring(7);

    modalOptions.value.show = false;

    toastAdd({
      id: toastId,
      group: "print:shipment:label:bulk",
      text: t("printer.printing_label", 99),
    });

    homeFetchRaw("shipments/export", {
      method: "POST",
      body: body,
      headers: {
        "Content-Type": "application/json",
      },
      silent: true,
    }).catch(() => {
      toastAdd({
        id: toastId,
        group: `export_failed`,
        text: t("export.failed_description"),
        error: true,
        duration: 10000,
      });
    });
  };

  const togglePrintModal = (entity?: any) => {
    modalOptions.value.show = !modalOptions.value.show;

    if (modalOptions.value.show) {
      modalOptions.value.entity = entity;
    } else {
      modalOptions.value.entity = null;
    }
  };

  return {
    getPrinters,
    getPrinterGroups,
    printLabel,
    printTransfer,
    modalOptions,
    togglePrintModal,
    printBulkLabels,
    printConsignment,
  };
};
